import { useState } from "react";
import addMonths from "date-fns/addMonths";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import { useUserProfile } from "@providers/profile";
import { HardwareSubscription } from "@providers/profile/ProfileProvider.model";
import { formatStripeCode } from "@utils/format";
import { formatPrice } from "@utils/pricing";
import { GlobalError } from "@components/primitives/input";
import { SubscriptionDetailProps, dateFormat } from "./Subscription.model";
import { SubscriptionNote } from "../Account.styled";

const nextPayment = (next_billing_amount: number, next_billing_date: string) => {
  if (next_billing_date) {
    return `${formatPrice(next_billing_amount)} on ${format(
      parseISO(next_billing_date),
      dateFormat
    )}`;
  } else {
    return "None";
  }
};

const Paused = ({ subscription }: { subscription: HardwareSubscription }) =>
  subscription.paused ? (
    <GlobalError>
      Subscription is paused{" "}
      {subscription.resumes_at
        ? `and resumes at ${format(parseISO(subscription.resumes_at), dateFormat)}`
        : "indefinitely"}
    </GlobalError>
  ) : null;

const SubscriptionDetail = ({ subscription, email }: SubscriptionDetailProps) => {
  const [error, setError] = useState<string>("");
  const {
    subscription_activation_date,
    credit,
    discount,
    paused,
    next_billing_amount,
    next_billing_date,
    final_billing_date,
    subscription_status,
    auto_renew,
  } = subscription;
  const {
    updateProfile,
    profileState: { authToken, profile },
    dispatch,
  } = useUserProfile();

  const [debounce, setDebounce] = useState<boolean>(false);
  const toggleAutoRenew = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setDebounce(true);
    try {
      await updateProfile(authToken, { ...profile, auto_renew: !auto_renew }, dispatch);
      subscription.auto_renew = !auto_renew;
    } catch (error) {
      setError("There was an error. Please contact the Member Experience Team for assistance.");
    }
    setDebounce(false);
  };

  return (
    <>
      <Paused subscription={subscription} />
      <dl>
        {email && (
          <>
            <dt>Email</dt>
            <dd>{email}</dd>
          </>
        )}
        <dt>Subscription Status</dt>
        <dd>{formatStripeCode(subscription_status)}</dd>
        <dt>Subscription Activation Date</dt>
        <dd>{format(parseISO(subscription_activation_date), dateFormat)}</dd>
        {discount && (
          <>
            <dt>Active Discount</dt>
            <dd>
              {discount.value} off{" "}
              {discount.duration === "repeating"
                ? `for ${discount.frequency} months`
                : discount.duration}
            </dd>
          </>
        )}
        <dt>Available Credit</dt>
        <dd>{formatPrice(credit)}</dd>
        {!paused && (
          <>
            <dt>Next Payment</dt>
            <dd>{nextPayment(next_billing_amount, next_billing_date)}</dd>
          </>
        )}
        {!paused && (
          <>
            <dt>Auto-Renew</dt>
            <dd>
              <form>
                <div onChange={toggleAutoRenew}>
                  <input
                    id="auto-renew-on"
                    name="auto-renew"
                    type="radio"
                    value={1}
                    disabled={debounce}
                    checked={auto_renew}
                  />
                  <label htmlFor="auto-renew-on">On</label>
                  <input
                    id="auto-renew-off"
                    name="auto-renew"
                    type="radio"
                    value={0}
                    disabled={debounce}
                    checked={!auto_renew}
                  />
                  <label htmlFor="auto-renew-off">Off</label>
                </div>
              </form>
              {!auto_renew && (
                <SubscriptionNote>
                  On {format(addMonths(parseISO(final_billing_date), 1), dateFormat)}, your subscription will end
                  and you will lose access to Mirror services.
                </SubscriptionNote>
              )}
            </dd>
          </>
        )}
      </dl>
      {error && <GlobalError>{error}</GlobalError>}
    </>
  );
};

export default SubscriptionDetail;
